// How the installed app is opened from outside: links with ?geo= / ?q= / ?lat=&lng=,
// the Search shortcut, and files handed over by the system (share sheet, "Open
// with", a launch while a window is already open).
//
// Files wait in a small CacheStorage "inbox" until /app/?import=1 reads them. The
// service worker fills it for a shared file (POST /share-target/); the page fills
// it for files from `launchQueue`. Both end in the normal import (synced.importFile).

import { parseLatLng } from './coordParse.js';

export const INBOX = 'share-target-inbox';
const INBOX_PREFIX = '/share-target/file/';

/** @param {number} lat @param {number} lng */
const finite = (lat, lng) =>
	Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;

/**
 * A geo: address (RFC 5870 plus the Android form): "geo:48.2,16.37",
 * "geo:48.2,16.37;u=20", "geo:0,0?q=48.2,16.37(Cafe)", "geo:0,0?q=Main+St+1".
 * @param {unknown} raw
 * @returns {{kind:'point',lat:number,lng:number,name?:string}|{kind:'search',q:string}|null}
 */
export function parseGeoUri(raw) {
	if (typeof raw !== 'string') return null;
	const s = raw.trim();
	const m = s.match(/^geo:([^?]*)(?:\?(.*))?$/i);
	if (!m) return null;
	const [lat, lng] = m[1].split(';')[0].split(',').map((v) => Number(v));
	let q = '';
	try {
		q = new URLSearchParams(m[2] || '').get('q') || '';
	} catch {
		q = '';
	}
	q = q.trim();
	if (q) {
		// "lat,lng(label)"
		const labelled = q.match(/^([^()]+?)\s*(?:\((.*)\))?$/);
		const point = labelled ? parseLatLng(labelled[1]) : null;
		if (point) return { kind: 'point', ...point, ...(labelled?.[2] ? { name: labelled[2].trim() } : {}) };
		return { kind: 'search', q };
	}
	// "geo:0,0" alone means "no place"; anything else is the place.
	if (finite(lat, lng) && !(lat === 0 && lng === 0)) return { kind: 'point', lat, lng };
	return null;
}

/**
 * Shared text or a pasted link: coordinates inside it when there are any (a geo:
 * address, a map link with @lat,lng or ?q=lat,lng, or plain "lat, lng"), else the
 * first line of words to search for.
 * @param {unknown} raw
 * @returns {{kind:'point',lat:number,lng:number,name?:string}|{kind:'search',q:string}|null}
 */
export function readSharedText(raw) {
	if (typeof raw !== 'string') return null;
	const text = raw.trim().slice(0, 2000);
	if (!text) return null;
	const geo = text.match(/geo:[^\s]+/i);
	if (geo) {
		const found = parseGeoUri(geo[0]);
		if (found) return found;
	}
	const pair = '(-?\\d{1,2}(?:\\.\\d+)?),\\s*(-?\\d{1,3}(?:\\.\\d+)?)';
	for (const re of [new RegExp('@' + pair), new RegExp('[?&](?:q|query|ll|daddr|destination)=' + pair)]) {
		const m = text.replace(/%2C/gi, ',').match(re);
		if (m && finite(Number(m[1]), Number(m[2]))) return { kind: 'point', lat: Number(m[1]), lng: Number(m[2]) };
	}
	const plain = parseLatLng(text);
	if (plain) return { kind: 'point', ...plain };
	const words = text
		.split('\n')
		.map((line) => line.replace(/\bhttps?:\/\/\S+/gi, '').trim())
		.find(Boolean);
	return words ? { kind: 'search', q: words.slice(0, 200) } : null;
}

/**
 * What an /app/ address asks for, or null for a plain /app/.
 * @param {URLSearchParams} params
 */
export function readLaunchParams(params) {
	if (params.has('import')) return { kind: 'import' };
	const geo = params.get('geo');
	if (geo) return parseGeoUri(geo.startsWith('geo:') ? geo : `geo:${geo}`) || readSharedText(geo);
	const lat = Number(params.get('lat'));
	const lng = Number(params.get('lng') ?? params.get('lon'));
	if (params.has('lat') && finite(lat, lng)) return { kind: 'point', lat, lng };
	const q = params.get('q');
	if (q && q.trim()) return readSharedText(q);
	if (params.has('search')) return { kind: 'focusSearch' };
	return null;
}

/**
 * Keeps files for /app/?import=1. Returns how many were kept.
 * @param {Iterable<File>} files
 */
export async function stashFiles(files) {
	const cache = await caches.open(INBOX);
	let kept = 0;
	for (const file of files) {
		if (!file || !file.size) continue;
		const headers = { 'Content-Type': file.type || 'application/octet-stream', 'X-File-Name': encodeURIComponent(file.name || 'shared') };
		await cache.put(`${INBOX_PREFIX}${Date.now()}-${kept}`, new Response(file, { headers }));
		kept++;
	}
	return kept;
}

/** The files waiting in the inbox (oldest first); the inbox is emptied. */
export async function takeFiles() {
	if (typeof caches === 'undefined') return [];
	try {
		const cache = await caches.open(INBOX);
		const files = [];
		for (const request of await cache.keys()) {
			const response = await cache.match(request);
			await cache.delete(request);
			if (!response) continue;
			const name = decodeURIComponent(response.headers.get('X-File-Name') || 'shared');
			const blob = await response.blob();
			files.push(new File([blob], name, { type: blob.type }));
		}
		return files;
	} catch (error) {
		console.warn('shared files not read', error);
		return [];
	}
}
